import type { ReactNode } from "react";
import { Card, Statistic, Typography } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

/** Colours a count only once it is worth acting on; zero is never alarming. */
export type StatTone = "default" | "warning" | "danger";

const TONE_COLOR: Record<StatTone, string | undefined> = {
  default: undefined,
  warning: "#d46b08",
  danger: "#cf1322",
};

interface StatCardProps {
  title: string;
  value: number | undefined;
  /**
   * Where the number came from: a register or report that applies the very predicate this
   * tile counted, so the rows the user lands on add up to the number they clicked. A tile
   * with nowhere honest to go (no permission for the drill-down) simply omits it.
   */
  to?: string;
  tone?: StatTone;
  /** What the tile actually counts, in the reader's terms — not a repeat of the title. */
  hint?: ReactNode;
  loading?: boolean;
}

export function StatCard({ title, value, to, tone = "default", hint, loading }: StatCardProps) {
  const count = value ?? 0;
  const color = count > 0 ? TONE_COLOR[tone] : undefined;

  const body = (
    <Card hoverable={!!to} className="glut-stat-card">
      <Statistic title={title} value={count} loading={loading} valueStyle={color ? { color } : undefined} />
      {(hint || to) && (
        <Typography.Text type="secondary" className="glut-stat-hint">
          {hint}
          {to && <ArrowRightOutlined className="glut-stat-arrow" />}
        </Typography.Text>
      )}
    </Card>
  );

  // A real anchor, not an onClick: middle-click, "open in new tab" and the status bar all
  // have to keep working on a tile that behaves like a link.
  return to ? (
    <Link to={to} className="glut-stat-link">
      {body}
    </Link>
  ) : (
    body
  );
}
